import { useMemo, useState } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useMultiCompanyLicenseTrend,
  useCustomerLicenseTrend,
} from "../hooks/useLicenseOverview";
import type {
  LicenseTrendMode,
  LicenseTrendPoint,
} from "../api/licenseOverviewApi";

interface Props {
  companyPartnerId?: number;
  companyName?: string;
  companyPartnerIds?: number[];
  companyNames?: string[];
}

const SERIES_META = [
  { key: "autodesk", label: "Autodesk", color: "#ef4444" },
  { key: "bluebeam", label: "Bluebeam", color: "#3b82f6" },
  { key: "sketchup", label: "SketchUp", color: "#10b981" },
  { key: "chaosgroup", label: "Chaosgroup", color: "#f59e0b" },
  { key: "adobe", label: "Adobe", color: "#06b6d4" },
  { key: "others", label: "Others", color: "#64748b" },
] as const;

type SeriesKey = (typeof SERIES_META)[number]["key"];

const formatNumber = (value: number | null | undefined): string => {
  if (value == null || Number.isNaN(value)) return "-";
  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 0,
  }).format(value);
};

const toDateInputValue = (value: Date): string => {
  const year = value.getFullYear();
  const month = `${value.getMonth() + 1}`.padStart(2, "0");
  const day = `${value.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const monthShift = (value: Date, offset: number) => {
  return new Date(value.getFullYear(), value.getMonth() + offset, 1);
};

const defaultMonthlyRange = () => {
  const end = new Date();
  const start = monthShift(end, -35);
  return {
    dateFrom: toDateInputValue(start),
    dateTo: toDateInputValue(end),
  };
};

const defaultYearlyRange = () => {
  const today = new Date();
  const start = new Date(today.getFullYear() - 9, 0, 1);
  return {
    dateFrom: toDateInputValue(start),
    dateTo: toDateInputValue(today),
  };
};

const normalizeRange = (dateFrom: string, dateTo: string) => {
  if (!dateFrom || !dateTo) {
    return { dateFrom, dateTo };
  }
  if (dateFrom <= dateTo) {
    return { dateFrom, dateTo };
  }
  return { dateFrom: dateTo, dateTo: dateFrom };
};

const getPeriodLabel = (mode: LicenseTrendMode, label: string) => {
  if (mode === "date_range") return label;
  return label;
};

export function CustomerLicenseTrendChart({
  companyPartnerId,
  companyName,
  companyPartnerIds,
  companyNames,
}: Props) {
  const [mode, setMode] = useState<LicenseTrendMode>("monthly");
  const monthlyRange = useMemo(() => defaultMonthlyRange(), []);
  const yearlyRange = useMemo(() => defaultYearlyRange(), []);
  const [customDateFrom, setCustomDateFrom] = useState<string>(
    monthlyRange.dateFrom
  );
  const [customDateTo, setCustomDateTo] = useState<string>(monthlyRange.dateTo);

  const effectiveRange = useMemo(() => {
    if (mode === "yearly") {
      return yearlyRange;
    }
    if (mode === "date_range") {
      const normalized = normalizeRange(
        customDateFrom || monthlyRange.dateFrom,
        customDateTo || monthlyRange.dateTo
      );
      return normalized;
    }
    return monthlyRange;
  }, [
    customDateFrom,
    customDateTo,
    mode,
    monthlyRange,
    yearlyRange,
  ]);

  const selectedCompanyPartnerIds = useMemo(
    () => Array.from(new Set((companyPartnerIds ?? [companyPartnerId]).filter((value): value is number => typeof value === "number" && value > 0))),
    [companyPartnerId, companyPartnerIds]
  );
  const selectedCompanyNames = useMemo(
    () => Array.from(new Set((companyNames ?? [companyName]).map((value) => value?.trim()).filter((value): value is string => Boolean(value)))),
    [companyName, companyNames]
  );

  const isMultiCompany = selectedCompanyPartnerIds.length > 1;
  const singleCompanyPartnerId = !isMultiCompany ? selectedCompanyPartnerIds[0] : undefined;

  const singleQuery = useCustomerLicenseTrend(singleCompanyPartnerId, {
    mode,
    dateFrom: effectiveRange.dateFrom,
    dateTo: effectiveRange.dateTo,
  });
  const multiQuery = useMultiCompanyLicenseTrend(
    isMultiCompany ? selectedCompanyPartnerIds : undefined,
    {
      mode,
      dateFrom: effectiveRange.dateFrom,
      dateTo: effectiveRange.dateTo,
    }
  );

  const data = isMultiCompany ? multiQuery.data : singleQuery.data;
  const isLoading = isMultiCompany ? multiQuery.isLoading : singleQuery.isLoading;
  const error = isMultiCompany ? multiQuery.error : singleQuery.error;

  const rows = useMemo(() => {
    return (data?.series ?? []).map((point: LicenseTrendPoint) => ({
      ...point,
      period_label: getPeriodLabel(mode, point.period_label),
    }));
  }, [data?.series, mode]);

  if (!isMultiCompany && !singleCompanyPartnerId) {
    return (
      <Card className="border border-dashed shadow-sm">
        <CardContent className="p-6 text-sm text-muted-foreground">
          Select one or more companies to view the active-license trend.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border shadow-sm">
      <CardHeader className="space-y-3 border-b border-border/70 pb-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div>
            <CardTitle className="text-lg">Active licenses by brands</CardTitle>
            <p className="mt-1 text-xs text-muted-foreground">
              End-of-period snapshot for{" "}
              {isMultiCompany
                ? selectedCompanyNames.join(", ")
                : companyName ?? `Company #${companyPartnerId}`}
            </p>
          </div>

          <div className="flex flex-col gap-3 md:items-end">
            <div className="flex flex-wrap items-end gap-3">
              <div className="min-w-[180px]">
                <label className="mb-1 block text-xs font-medium text-muted-foreground">
                  Time grouping
                </label>
                <Select
                  value={mode}
                  onValueChange={(value) => setMode(value as LicenseTrendMode)}
                >
                  <SelectTrigger className="h-9 w-full">
                    <SelectValue placeholder="Select mode" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yearly">Yearly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="date_range">Date range</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {mode === "date_range" ? (
                <div className="flex flex-wrap items-end gap-2">
                  <div className="flex flex-col">
                    <label className="mb-1 text-xs font-medium text-muted-foreground">
                      From
                    </label>
                    <input
                      type="date"
                      className="h-9 rounded-md border bg-background px-2 text-xs shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                      value={customDateFrom}
                      onChange={(event) => setCustomDateFrom(event.target.value)}
                      max={customDateTo}
                    />
                  </div>
                  <div className="flex flex-col">
                    <label className="mb-1 text-xs font-medium text-muted-foreground">
                      To
                    </label>
                    <input
                      type="date"
                      className="h-9 rounded-md border bg-background px-2 text-xs shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                      value={customDateTo}
                      onChange={(event) => setCustomDateTo(event.target.value)}
                      min={customDateFrom}
                    />
                  </div>
                </div>
              ) : null}
            </div>

            <p className="text-[11px] text-muted-foreground">
              Tooltips show active license counts for each brand group in the selected period.
            </p>
          </div>
        </div>
      </CardHeader>

      <CardContent className="h-[380px] pt-4">
        {isLoading ? (
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
            Loading active-license trend...
          </div>
        ) : error ? (
          <div className="flex h-full items-center justify-center text-sm text-destructive">
            Failed to load active-license trend.
          </div>
        ) : !rows.length ? (
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
            No active-license trend records for this company.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={rows} margin={{ left: 8, right: 16, top: 16, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="period_label" minTickGap={24} />
              <YAxis allowDecimals={false} />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload?.length) return null;
                  const row = payload[0]?.payload as LicenseTrendPoint;
                  return (
                    <div className="w-72 space-y-2 rounded-md border bg-background p-3 text-xs shadow-sm">
                      <div className="text-sm font-semibold">
                        {String(label)}
                      </div>
                      <div className="space-y-1">
                        {SERIES_META.map((series) => {
                          const value = row?.[series.key as SeriesKey];
                          return (
                            <div
                              key={series.key}
                              className="flex items-center justify-between gap-3"
                            >
                              <span className="flex items-center gap-2">
                                <span
                                  className="inline-block h-2.5 w-2.5 rounded-full"
                                  style={{ backgroundColor: series.color }}
                                />
                                <span>{series.label}</span>
                              </span>
                              <span className="font-medium">
                                {formatNumber(value)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                      <div className="border-t pt-2 text-[11px] text-muted-foreground">
                        Total active licenses:{" "}
                        <span className="font-medium text-foreground">
                          {formatNumber(row?.total_active_licenses)}
                        </span>
                      </div>
                    </div>
                  );
                }}
              />
              <Legend
                formatter={(value: any, entry: any) => {
                  const label = String(value);
                  const color = entry?.color ?? "#64748b";
                  return <span style={{ color }}>{label}</span>;
                }}
              />
              {SERIES_META.map((series) => (
                <Line
                  key={series.key}
                  type="monotone"
                  dataKey={series.key}
                  name={series.label}
                  stroke={series.color}
                  strokeWidth={2.5}
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}

export default CustomerLicenseTrendChart;
